import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, useConfig } from "@/hooks";
import { useUpdateAccountMutation } from "@/redux/services/account/accountApi";
import { UiTour } from "./UiTour";
import { CoreTutorialModal, ContextualTutorialModal } from "./TutorialModal";
import {
  getContextualModuleFromPath,
  getCoreStepsForUser,
  getModuleCopy,
  getNormalizedTutorialProgress,
  getUiTourSteps,
  isAdminTutorialEligible,
  isModuleTutorialEligible,
  maybeMarkCoreStepFromPath,
} from "./tutorial.utils";
import { AUTO_OPEN_ONBOARDING_HELP, UI_TOUR_VERSION } from "./constants";

const OPEN_TUTORIAL_EVENT = "attune:tutorial-open";
const OPEN_CREATE_MODAL_EVENT = "attune:tutorial-open-create";

export function TutorialOrchestrator() {
  const { user, initialized } = useAuth();
  const { appType, configs, loading: configLoading } = useConfig();
  const featureFlags = configs?.featureFlags || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [updateAccount] = useUpdateAccountMutation();

  const [modalState, setModalState] = useState({
    core: false,
    contextual: false,
    contextualModule: null,
  });
  const [manualOpenRequested, setManualOpenRequested] = useState(false);
  const [uiTourVisible, setUiTourVisible] = useState(false);
  const [openChecklistAfterUiTour, setOpenChecklistAfterUiTour] = useState(false);

  const [saving, setSaving] = useState(false);

  const hasTutorialInfo = useMemo(() => {
    return Boolean(
      user &&
        user.tutorialProgress &&
        typeof user.tutorialProgress === "object" &&
        Object.keys(user.tutorialProgress).length > 0
    );
  }, [user]);

  // Testing behavior: if tutorial data is missing, force tutorial visibility checks.
  const eligible = useMemo(
    () => isAdminTutorialEligible(user) || !hasTutorialInfo,
    [user, hasTutorialInfo]
  );
  const progress = useMemo(
    () => getNormalizedTutorialProgress(user?.tutorialProgress),
    [user?.tutorialProgress]
  );
  const canRenderTutorialUi = eligible || manualOpenRequested;

  const coreSteps = useMemo(() => {
    const steps = getCoreStepsForUser(user, appType);
    return steps.map((step) => ({
      ...step,
      completed: Boolean(progress.coreSteps?.[step.id]),
    }));
  }, [progress.coreSteps, user, appType]);
  const uiTourSteps = useMemo(() => getUiTourSteps(appType), [appType]);

  const updateProgress = async (nextProgress) => {
    if (!nextProgress || saving) return;
    setSaving(true);
    try {
      await updateAccount({ tutorialProgress: nextProgress }).unwrap();
    } catch (error) {
      // Fail silently so onboarding UX doesn't hard break if update fails.
      console.error("Failed to persist tutorial progress", error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!AUTO_OPEN_ONBOARDING_HELP) return;
    if (!initialized || configLoading || !user || !appType || !eligible) {
      return;
    }

    if (!progress.uiTourCompleted || uiTourVisible) {
      return;
    }

    if (!progress.coreAdminOnboardingCompleted) {
      setModalState((prev) => ({ ...prev, core: true }));
    }
  }, [
    initialized,
    configLoading,
    user,
    appType,
    eligible,
    progress.uiTourCompleted,
    progress.coreAdminOnboardingCompleted,
    uiTourVisible,
  ]);

  useEffect(() => {
    if (!AUTO_OPEN_ONBOARDING_HELP) return;
    if (!initialized || configLoading || !user || !appType || !canRenderTutorialUi) {
      return;
    }

    const sessionKey = `attune:tutorial:login-opened:${user._id}`;
    const alreadyShown = sessionStorage.getItem(sessionKey) === "true";
    if (alreadyShown) return;

    if (!progress.uiTourCompleted) {
      setOpenChecklistAfterUiTour(true);
      setUiTourVisible(true);
    } else {
      setModalState((prev) => ({ ...prev, core: true }));
    }
    sessionStorage.setItem(sessionKey, "true");
  }, [
    initialized,
    configLoading,
    user,
    appType,
    canRenderTutorialUi,
    progress.uiTourCompleted,
  ]);

  useEffect(() => {
    if (!initialized || !user || !eligible) return;
    const next = maybeMarkCoreStepFromPath(location.pathname, progress);
    if (!next) return;
    updateProgress(next);
  }, [initialized, user, eligible, location.pathname, progress]);

  useEffect(() => {
    if (!AUTO_OPEN_ONBOARDING_HELP) return;
    if (
      !initialized ||
      configLoading ||
      !user ||
      !eligible ||
      !progress.uiTourCompleted ||
      !progress.coreAdminOnboardingCompleted ||
      modalState.core ||
      uiTourVisible
    ) {
      return;
    }

    const module = getContextualModuleFromPath(location.pathname);
    if (!module) return;

    const canShowModule = isModuleTutorialEligible({
      module,
      appType,
      featureFlags,
      progress,
    });

    if (!canShowModule) return;

    setModalState((prev) => ({
      ...prev,
      contextual: true,
      contextualModule: module,
    }));

    updateProgress({
      ...progress,
      contextual: {
        ...progress.contextual,
        [module]: true,
      },
      updatedAt: new Date().toISOString(),
    });
  }, [
    initialized,
    configLoading,
    user,
    eligible,
    progress,
    appType,
    featureFlags,
    location.pathname,
    modalState.core,
    uiTourVisible,
  ]);

  useEffect(() => {
    const handleOpenTutorial = (event) => {
      const type = event?.detail?.type || "core";
      if (type === "checklist") {
        setManualOpenRequested(true);
        setModalState((prev) => ({ ...prev, core: true }));
        return;
      }

      if (type === "core" || type === "full") {
        setManualOpenRequested(true);
        setOpenChecklistAfterUiTour(true);
        setUiTourVisible(true);
      }
    };

    window.addEventListener(OPEN_TUTORIAL_EVENT, handleOpenTutorial);
    return () => {
      window.removeEventListener(OPEN_TUTORIAL_EVENT, handleOpenTutorial);
    };
  }, []);

  const handleStepComplete = async (step) => {
    const nextCoreSteps = {
      ...progress.coreSteps,
      [step.id]: true,
    };
    const allComplete = Object.values(nextCoreSteps).every(Boolean);

    await updateProgress({
      ...progress,
      coreSteps: nextCoreSteps,
      coreAdminOnboardingCompleted: allComplete,
      completedAt: allComplete ? new Date().toISOString() : progress.completedAt,
      updatedAt: new Date().toISOString(),
    });
  };

  const handleSkipCore = async () => {
    await updateProgress({
      ...progress,
      coreAdminOnboardingCompleted: true,
      completedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setModalState((prev) => ({ ...prev, core: false }));
  };

  const contextualCopy = getModuleCopy(modalState.contextualModule);

  const openCreateModal = (entity) => {
    if (typeof window === "undefined" || !entity) return;
    window.dispatchEvent(
      new CustomEvent(OPEN_CREATE_MODAL_EVENT, {
        detail: { entity },
      })
    );
  };

  const handleUiTourFinish = async () => {
    await updateProgress({
      ...progress,
      uiTourCompleted: true,
      uiTourVersion: UI_TOUR_VERSION,
      updatedAt: new Date().toISOString(),
    });
    setUiTourVisible(false);

    if (openChecklistAfterUiTour || !progress.coreAdminOnboardingCompleted) {
      setModalState((prev) => ({ ...prev, core: true }));
    }
    setOpenChecklistAfterUiTour(false);
  };

  if (!canRenderTutorialUi) {
    return null;
  }

  return (
    <>
      <UiTour
        visible={uiTourVisible}
        steps={uiTourSteps}
        onClose={handleUiTourFinish}
        onSkip={handleUiTourFinish}
        onComplete={handleUiTourFinish}
      />
      <CoreTutorialModal
        visible={modalState.core}
        steps={coreSteps}
        onClose={() => setModalState((prev) => ({ ...prev, core: false }))}
        onSkip={handleSkipCore}
        onStepNavigate={(step) => {
          openCreateModal(step.createEntity);
          setModalState((prev) => ({ ...prev, core: false }));
        }}
        onStepComplete={handleStepComplete}
      />

      <ContextualTutorialModal
        visible={modalState.contextual}
        title={contextualCopy.title}
        body={contextualCopy.body}
        ctaLabel={contextualCopy.ctaLabel}
        onClose={() =>
          setModalState((prev) => ({
            ...prev,
            contextual: false,
            contextualModule: null,
          }))
        }
        onNavigate={() => {
          const moduleToEntityMap = {
            invoices: "invoice",
            manifests: "manifest",
            payouts: "payout",
          };
          const createEntity = moduleToEntityMap[modalState.contextualModule];
          if (createEntity) {
            openCreateModal(createEntity);
          } else if (contextualCopy.ctaPath) {
            navigate(contextualCopy.ctaPath);
          }
          setModalState((prev) => ({
            ...prev,
            contextual: false,
            contextualModule: null,
          }));
        }}
      />
    </>
  );
}

export default TutorialOrchestrator;
